// ประกาศ · ขอเวร — pure helpers for the broadcast feature. No I/O.
//
// ⚠️ REACH IS THE POINT OF THIS FILE. Measured live 2026-08-20: of 406 active
// doctors, 397 have a working account and 9 do not; of those 397, 306 hold a
// credential and 100 have never set one up. `can_login` from the server means
// "has an ACTIVE user row" — user.password is access:internal and cannot be read
// inside a Xano function stack, so "has finished signing up" is NOT knowable
// live and is deliberately not shown. Delivery is portal-only by the owner's
// decision, so the composer's job is to state that honestly BEFORE sending —
// never to let a manager believe a broadcast went further than it did.
// `reachSummary()` exists so that number is phrased the same way everywhere.
//
// The audience itself is resolved SERVER-side (Xano fn `broadcast_audience`) and
// frozen onto the row as `audience_doctor[]`. Nothing here re-derives it: a
// second implementation of the spec-matching rule would drift from the one that
// actually decided who got the message.
import { NO_SPEC, specLabel } from './dentist.js'

/* ── status ──────────────────────────────────────────────────────────────── */

export const ANNOUNCEMENT_STATUS = {
  open:   { label: 'กำลังแสดง', tone: 'emerald' },
  closed: { label: 'ปิดแล้ว',   tone: 'slate' },
}

export const SHIFT_STATUS = {
  open:      { label: 'กำลังรับสมัคร', tone: 'amber' },
  closed:    { label: 'เลือกหมอแล้ว',  tone: 'emerald' },
  cancelled: { label: 'ยกเลิก',        tone: 'slate' },
}

export const RESPONSE_META = {
  none:       { label: 'ยังไม่ตอบ', tone: 'slate' },
  interested: { label: 'สนใจ',      tone: 'emerald' },
  declined:   { label: 'ไม่สะดวก',  tone: 'slate' },
}

export const OUTCOME_META = {
  none:         { label: 'รอผู้จัดการยืนยัน', tone: 'amber' },
  accepted:     { label: 'ได้รับเวรนี้',       tone: 'emerald' },
  not_accepted: { label: 'ไม่ได้รับเวรนี้',    tone: 'rose' },
}

const TONE_CLASS = {
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  amber:   'bg-amber-50 text-amber-700 ring-amber-100',
  rose:    'bg-rose-50 text-rose-700 ring-rose-100',
  slate:   'bg-slate-100 text-slate-600 ring-slate-200',
}
export const toneClass = (tone) => TONE_CLASS[tone] || TONE_CLASS.slate

export const statusMeta = (kind, status) => {
  const table = kind === 'shift_request' ? SHIFT_STATUS : ANNOUNCEMENT_STATUS
  return table[status] || { label: status || '—', tone: 'slate' }
}

/* ── audience ────────────────────────────────────────────────────────────── */

// The three targeting modes are not exclusive in storage — target_all wins, and
// named doctors are always UNIONed on top of a spec match. This mirrors
// broadcast_audience exactly; if that changes, change both.
export function audienceSummary(row) {
  if (!row) return '—'
  if (row.target_all) return 'ทันตแพทย์ทุกท่าน'
  const specs = (row.target_spec || []).filter(Boolean)
  const docs = (row.target_doctor || []).filter(Boolean)
  const parts = []
  if (specs.length) parts.push(specs.map((s) => specLabel(s)).join(' · '))
  if (docs.length) parts.push(`เลือกเอง ${docs.length} ท่าน`)
  return parts.length ? parts.join(' + ') : '—'
}

// Is this targeting selection sendable at all? Mirrors the Xano precondition, so
// the button disables rather than the server rejecting after a round trip.
export const hasAudience = (t) => Boolean(
  t?.target_all || (t?.target_spec || []).length > 0 || (t?.target_doctor || []).length > 0,
)

/* ── reach ───────────────────────────────────────────────────────────────── */

// `preview` is the raw response from POST /api/hr/broadcast/preview (Xano fn
// broadcast_audience). Everything the composer shows about reach comes from
// here, so the wording cannot drift between the preview and the sent record.
export function reachSummary(preview) {
  const total = Number(preview?.total || 0)
  const reachable = Number(preview?.reachable || 0)
  const unreachable = Math.max(0, total - reachable)
  const skipped = Number(preview?.no_spec_skipped || 0)
  return {
    total,
    reachable,
    unreachable,
    skipped,
    // The one sentence this whole feature turns on.
    headline: total === 0
      ? 'ไม่พบทันตแพทย์ที่ตรงกับเงื่อนไขนี้'
      : `ส่งถึง ${reachable} จาก ${total} ท่าน`,
    // Shown only when it is non-zero — an all-reachable send should not carry a
    // warning that says "0 people cannot see this".
    warning: unreachable > 0
      ? `อีก ${unreachable} ท่านยังไม่ได้เปิดใช้บัญชี จึงจะไม่เห็นประกาศนี้`
      : '',
    // The ไม่ระบุ bucket: doctors with an empty spec[] that a spec-targeted send
    // cannot reach. Measured 2026-08-20 this is 0 — every ACTIVE doctor carries at
    // least one spec, so the note should normally never render. It stays because
    // the ไม่ระบุ chip on /manager/dentist is real (it counts inactive doctors too)
    // and a future doctor created without a spec would otherwise vanish silently.
    skippedNote: skipped > 0
      ? `ไม่รวมทันตแพทย์ ${skipped} ท่านที่ยังไม่ได้ระบุความเชี่ยวชาญ`
      : '',
  }
}

// The [ดูรายชื่อ] list behind the warning — who cannot see this, and why.
// Sorted by code so it reads like the directory the manager already knows.
export const unreachableDoctors = (preview) => (
  (preview?.doctors || [])
    .filter((d) => !d.can_login)
    .sort((a, b) => String(a.code || '').localeCompare(String(b.code || ''), 'th'))
)

/* ── spec options ────────────────────────────────────────────────────────── */

// Spec chips for the audience picker, derived FROM THE DATA — `spec[]` is free
// text, not an enum (live rows hold `d-aligner` and `สปสช`), so a hard-coded
// list would quietly drop real doctors. Same derivation as the ทันตแพทย์
// directory's filter, minus the ไม่ระบุ bucket: you cannot target "no spec".
export function specOptions(doctors) {
  const count = new Map()
  for (const d of doctors || []) {
    if (d?.active === false) continue
    for (const s of (Array.isArray(d?.spec) ? d.spec : []).filter(Boolean)) {
      count.set(String(s), (count.get(String(s)) || 0) + 1)
    }
  }
  return [...count.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'th'))
    .map(([value, n]) => ({ value, label: `${specLabel(value)} (${n})` }))
}

export { NO_SPEC }

/* ── shift request window ────────────────────────────────────────────────── */

// start_date/end_date are epoch ms, matching the appointment convention. The
// browser is Asia/Bangkok and so is the clinic, so LOCAL getters are the correct
// conversion — no offset arithmetic anywhere.
const pad = (n) => String(n).padStart(2, '0')

export function hhmm(ms) {
  if (ms == null || ms === '') return ''
  const d = new Date(Number(ms))
  if (Number.isNaN(d.getTime())) return ''
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function shiftWindowLabel(row) {
  const a = hhmm(row?.start_date)
  const b = hhmm(row?.end_date)
  return a && b ? `${a}–${b}` : (a || b || '—')
}

// 'YYYY-MM-DD' + 'HH:MM' → epoch ms, built in local (Bangkok) time.
export function timeOnDay(dayKey, time) {
  const [y, m, d] = String(dayKey || '').split('-').map(Number)
  const [hh, mi] = String(time || '00:00').split(':').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d, hh || 0, mi || 0, 0, 0).getTime()
}

/* ── the roster gap ──────────────────────────────────────────────────────── */

// 🚨 `shift_request` is its own table and its lifecycle never writes
// `appointment` (owner's decision, 2026-08-20) — picking a doctor does not
// silently roster them. Closing the gap is an explicit button in
// ShiftRequestDrawer that POSTs to the ROSTER's own endpoint
// (/api/hr/schedule/doctor), which is what fires appointment_update.
//
// ⚠️ There used to be a rosterDeepLink() here building
// `/manager/schedule/doctor?branch&date&doctor&start&end` and described as
// "prefilled". DoctorSchedule reads NO query params, so it prefilled nothing
// and the button looked broken. Removed rather than fixed: the write is better
// done in place than by sending someone to retype it.
export const ROSTER_GAP_NOTE = 'ยังไม่ได้ลงในตารางเวร — คนไข้ยังจองหมอท่านนี้ไม่ได้'
